import "./style.scss";

import DocumentationLink from "components/shared/DocumentationLink";
import FiltersAutocomplete, { FilterOption } from "components/shared/FiltersAutocomplete";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import PeopleAutocompleteField from "components/shared/PeopleAutocompleteField";
import WorkSelectField from "components/TitleFormContainer/WorkSelectField";
import { getCountries } from "countries";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";
import type { ITransferTerm } from "store/transferTermsStore";

import type { ITransferTermFormData } from "..";
import { checkSimilarTermExists, formatCountriesForFilter } from "../utils";

interface INewTermStep {
    indexStep: number;
    stepTitle: string;
    stepSubtitle: string;
    useFormData: ITransferTermFormData;
    /**
     * Array with countries chosen within form.
     */
    chosenCountries: { id: string; name: string }[];
    setChosenCountries: any;
    mode: "new" | "edit";
    /**
     * Opens Transferee form when they choose to create Transferee.
     */
    setAddNewTransferee: Dispatch<SetStateAction<boolean | undefined>>;
    from: "transfereePage" | "termDrawer" | undefined;
    /**
     * All existing transfer terms (needed to check if similar term exists).
     */
    allTransferTerms: ITransferTerm[];
    /**
     * Term data (on edit).
     */
    term: ITransferTerm | undefined;
    haveCountriesChanged: boolean;
}

/**
 * First step of the TransferTermForm.
 */
const NewTermStep = ({
    indexStep,
    stepSubtitle,
    stepTitle,
    useFormData,
    chosenCountries,
    setChosenCountries,
    mode,
    setAddNewTransferee,
    from,
    allTransferTerms,
    term,
    haveCountriesChanged,
}: INewTermStep) => {
    const [t] = useTranslation();
    const { control, watch, errors, setValue, setError, clearErrors } = useFormData;

    const { transfereesAutocomplete, transferee } = useAppSelector((state) => state.transferees);

    const countriesOptions = formatCountriesForFilter(getCountries());
    const currentTransferee = watch("transferee");

    const currentWork = watch("work");

    const countriesErrorCondition = useMemo(() => {
        return mode === "edit" && haveCountriesChanged && !chosenCountries.length;
    }, [haveCountriesChanged, chosenCountries]);

    useEffect(() => {
        if (countriesErrorCondition) {
            setError?.("countryCodes", { message: "" });
        } else {
            clearErrors?.("countryCodes");
        }
    }, [countriesErrorCondition]);

    const [similarTermExists, setSimilarTermExists] = useState(false);

    // Checks if similar term exists to display reference input.
    useEffect(() => {
        if (currentWork && currentTransferee && chosenCountries.length && mode === "new") {
            const similarTermExists = checkSimilarTermExists(
                chosenCountries,
                allTransferTerms,
                currentWork.id,
                currentTransferee.id,
                "form",
            );
            if (similarTermExists) {
                setSimilarTermExists(true);
            }
        } else {
            if (mode === "new") {
                setSimilarTermExists(false);
                setValue("reference", "");
            }
        }
    }, [currentWork, currentTransferee, chosenCountries]);

    // Opens transferee form.
    useEffect(() => {
        if (currentTransferee && currentTransferee?.id === -1) {
            setAddNewTransferee(true);
            setValue("transferee", null);
        }
    }, [currentTransferee]);

    return (
        <>
            <h3 className="form-section-title first">
                {" "}
                {indexStep + 1}. {stepTitle}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                <WorkSelectField
                    control={control}
                    error={errors.work}
                    required
                    disabled={mode === "edit"}
                    extraOption={currentWork}
                />
                <PeopleAutocompleteField
                    control={control}
                    controlName="transferee"
                    label={t("TransferTerms.TermTable.Transferee")}
                    errors={errors.transferee}
                    required
                    data={transfereesAutocomplete}
                    type={"transferees"}
                    watch={watch}
                    disabled={mode === "edit" || (from === "transfereePage" && !!transferee)}
                    addButton={t("Transferees.NewTransferee")}
                />
                <FiltersAutocomplete
                    label={t("TransferTerms.TermTable.Country")}
                    filtersOptions={countriesOptions}
                    placeholder={t("TransferTerms.TermForm.ChooseCountriesLabel")}
                    chosenOptions={chosenCountries}
                    setChosenOptions={setChosenCountries}
                    optionsLimit={-1}
                    required
                    errorCondition={countriesErrorCondition}
                    errorMessage={t("TransferTerms.TermForm.CountriesErrorMessage")}
                />
                {mode === "edit" ? (
                    <div className="edit-reference-info">
                        <TextFormComponent
                            label={t("TransferTerms.TermForm.Reference")}
                            control={control}
                            controlName="reference"
                            error={errors.reference}
                            placeholder={t("TransferTerms.TermForm.DefaultReferencePlaceholder", {
                                number: term && term.id,
                            })}
                            infoTooltip={{
                                text: t("TransferTerms.TermForm.DefaultReferenceInfoTooltip"),
                                type: "info",
                            }}
                        />
                        {!term?.reference && (
                            <span className="edit-info">{t("TransferTerms.TermForm.DefaultReferenceInfo")}</span>
                        )}
                    </div>
                ) : (
                    mode === "new" &&
                    similarTermExists && (
                        <div className="reference-info">
                            <div className="text">
                                <span>{t("TransferTerms.TermForm.ReferenceInfo1")}</span>
                                <span>{t("TransferTerms.TermForm.ReferenceInfo2")}</span>
                            </div>
                            <TextFormComponent
                                label={t("TransferTerms.TermForm.Reference")}
                                control={control}
                                controlName="reference"
                                error={errors.reference}
                                placeholder="5TYI6FH"
                                showOptionalMark
                            />
                        </div>
                    )
                )}
            </div>
        </>
    );
};

export default NewTermStep;
